import * as React from 'react'
import { cn } from '@/lib/utils'

type Tone = 'default' | 'orange' | 'green' | 'blue' | 'red' | 'amber' | 'violet' | 'cyan' | 'neutral'

const TONE: Record<Tone, string> = {
  default: 'bg-neutral-100 text-neutral-700 ring-neutral-200 dark:bg-neutral-50 dark:bg-white/5 dark:text-neutral-700 dark:text-white/70 dark:ring-neutral-200 dark:ring-white/10',
  neutral: 'bg-neutral-100 text-neutral-600 ring-neutral-200 dark:bg-neutral-500/10 dark:text-neutral-300 dark:ring-neutral-500/20',
  orange: 'bg-orange-50 text-orange-700 ring-orange-200 dark:bg-orange-500/10 dark:text-orange-300 dark:ring-orange-500/20',
  green: 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20',
  blue: 'bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-500/20',
  red: 'bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/20',
  amber: 'bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20',
  violet: 'bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/20',
  cyan: 'bg-cyan-50 text-cyan-700 ring-cyan-200 dark:bg-cyan-500/10 dark:text-cyan-300 dark:ring-cyan-500/20',
}

interface StatusPillProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: Tone
  dot?: boolean
}

export function StatusPill({ className, tone = 'default', dot = false, children, ...props }: StatusPillProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset whitespace-nowrap',
        TONE[tone],
        className
      )}
      {...props}
    >
      {dot && <span className={cn('h-1.5 w-1.5 rounded-full', dotColor(tone))} />}
      {children}
    </span>
  )
}

function dotColor(tone: Tone): string {
  const map: Record<Tone, string> = {
    default: 'bg-neutral-400 dark:bg-white/40',
    neutral: 'bg-neutral-400 dark:bg-neutral-300',
    orange: 'bg-orange-500 dark:bg-orange-300',
    green: 'bg-emerald-500 dark:bg-emerald-300',
    blue: 'bg-blue-500 dark:bg-blue-300',
    red: 'bg-rose-500 dark:bg-rose-300',
    amber: 'bg-amber-500 dark:bg-amber-300',
    violet: 'bg-violet-500 dark:bg-violet-300',
    cyan: 'bg-cyan-500 dark:bg-cyan-300',
  }
  return map[tone]
}

// Common status → tone mapping for stipend/leave/wfh/task status badges.
export function statusTone(status?: string | null): Tone {
  if (!status) return 'neutral'
  const s = status.toUpperCase()
  if (s === 'APPROVED' || s === 'COMPLETED' || s === 'PRESENT' || s === 'ACTIVE' || s === 'EXCELLENT') return 'green'
  if (s === 'PENDING' || s === 'IN_PROGRESS' || s === 'WFH_NO_LOG') return 'amber'
  if (s === 'REJECTED' || s === 'ABSENT' || s === 'LOW' || s === 'OVERDUE' || s === 'CANCELLED') return 'red'
  if (s === 'WFH' || s === 'AVERAGE') return 'blue'
  if (s === 'HOLIDAY' || s === 'WEEKEND') return 'violet'
  if (s === 'GOOD') return 'cyan'
  return 'neutral'
}
